// src/pages/StaffLoginPage.tsx
// Staff-only login page - Guest login is at /login
import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  useTheme,
  Tooltip,
} from '@mui/material';
import { Visibility, VisibilityOff, Hotel as HotelIcon, Business as BusinessIcon, LightMode, DarkMode } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { UserRole } from '../types/chat';

// Flag country codes for languages
const languageFlags: Record<string, string> = {
  en: 'gb',
  tr: 'tr',
  de: 'de',
  fr: 'fr',
  ru: 'ru',
  ar: 'ae',
};

// Flag icon component
const FlagIcon: React.FC<{ countryCode: string; size?: number }> = ({ countryCode, size = 24 }) => {
  return (
    <Box
      component="img"
      src={`https://flagcdn.com/w${size}/${countryCode}.png`}
      alt=""
      sx={{
        width: size,
        height: size * 0.75,
        objectFit: 'cover',
        borderRadius: '2px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        border: '0.5px solid rgba(0, 0, 0, 0.1)',
      }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        target.src = `https://flagcdn.com/${countryCode}.svg`;
      }}
    />
  );
};

interface Hotel {
  id: string;
  name: string;
  code: string;
}

const StaffLoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const surfaceBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.92) 100%)';
  const surfaceBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.04)';
  const primaryTextColor = isDark ? '#f8fafc' : '#1f2937';
  const secondaryTextColor = isDark ? 'rgba(248, 250, 252, 0.75)' : '#6b7280';

  const handleThemeToggle = () => {
    const newMode = isDark ? 'light' : 'dark';
    localStorage.setItem('themeMode', newMode);
    window.dispatchEvent(new CustomEvent('themeChange', { detail: newMode }));
  };

  // Staff login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedHotel, setSelectedHotel] = useState('');
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const handleClickShowPassword = () => {
    const input = passwordInputRef.current;
    const cursorPosition = input?.selectionStart || 0;
    setShowPassword(!showPassword);
    setTimeout(() => {
      if (input) {
        input.focus();
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Fetch hotels on component mount
  React.useEffect(() => {
    fetchAllHotels();
  }, []);

  const fetchAllHotels = async () => {
    setHotelsLoading(true);
    try {
      const [localResponse, pmsResponse] = await Promise.allSettled([
        api.get('/hotels/public/list'),
        api.get('/gsc-api/hotels'),
      ]);

      const localHotels = localResponse.status === 'fulfilled' ? localResponse.value.data : [];
      const pmsHotels = pmsResponse.status === 'fulfilled' ? pmsResponse.value.data : [];

      const hotelMap = new Map<string, Hotel>();

      localHotels.forEach((hotel: Hotel) => {
        hotelMap.set(hotel.id, hotel);
      });

      pmsHotels.forEach((pmsHotel: any) => {
        const existingLocalHotel = localHotels.find((h: Hotel) =>
          h.code === pmsHotel.code || h.id === pmsHotel.id
        );

        if (!existingLocalHotel) {
          hotelMap.set(pmsHotel.id, {
            id: pmsHotel.id,
            name: pmsHotel.name,
            code: pmsHotel.code,
          });
        }
      });

      const combined = Array.from(hotelMap.values());
      setHotels(combined);
      setError('');
    } catch (error) {
      console.error('Failed to fetch hotels:', error);
      setError('Failed to fetch hotels');
      setHotels([]);
    } finally {
      setHotelsLoading(false);
    }
  };

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError(t('login.emailRequired'));
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError(t('login.emailInvalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError(t('login.passwordRequired'));
      return false;
    }
    if (value.length < 6) {
      setPasswordError(t('login.passwordMinLength'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setIsLoading(true);

    try {
      const loginPayload: any = { email, password };
      if (selectedHotel && selectedHotel.trim() !== '') {
        loginPayload.hotelId = selectedHotel;
      }

      const response = await api.post('/auth/login', loginPayload);

      const { user, accessToken, refreshToken } = response.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      const targetPath = user?.role === UserRole.SUPER_ADMIN ? '/super-admin' : '/dashboard';
      window.location.href = targetPath;
    } catch (error: any) {
      console.error('[StaffLoginPage] Login error:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Login failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (event: any) => {
    i18n.changeLanguage(event.target.value);
  };

  // Re-validate error messages when language changes
  React.useEffect(() => {
    if (emailError) validateEmail(email);
    if (passwordError) validatePassword(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const getLanguageCode = (lang: string) => {
    return lang.split('-')[0].toLowerCase();
  };

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.9)',
      color: isDark ? '#f8fafc' : '#1f2937',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: 1.5,
        transition: 'all 0.3s ease',
      },
      '&:hover': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 8px 22px rgba(0, 174, 239, 0.2)'
          : '0 4px 12px rgba(0, 174, 239, 0.12)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 12px 30px rgba(0, 174, 239, 0.25)'
          : '0 8px 20px rgba(0, 174, 239, 0.18)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2.5,
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: isDark ? 'rgba(248, 250, 252, 0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: accentColor,
        fontWeight: 700,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: isDark ? '#f8fafc' : '#1f2937',
    },
    '& .MuiFormHelperText-root': {
      color: isDark ? '#fca5a5' : undefined,
    },
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: surfaceBg,
          backdropFilter: 'blur(18px)',
          border: surfaceBorder,
          color: primaryTextColor,
          position: 'relative',
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.55)'
            : '0 30px 80px rgba(15, 23, 42, 0.12)',
        }}
      >
        {/* Language Selector & Theme Toggle */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 24,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Tooltip title={isDark ? t('sidebar.light') : t('sidebar.dark')}>
            <IconButton
              onClick={handleThemeToggle}
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 23, 42, 0.1)'}`,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.05)',
                color: isDark ? '#f8fafc' : '#0f172a',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.18)' : 'rgba(0, 174, 239, 0.18)',
                },
              }}
            >
              {isDark ? <LightMode sx={{ fontSize: 22 }} /> : <DarkMode sx={{ fontSize: 22 }} />}
            </IconButton>
          </Tooltip>
          <FormControl
            size="small"
            sx={{
              width: 42,
              height: 42,
              minWidth: 42,
              minHeight: 42,
              padding: 0,
              margin: 0,
              boxSizing: 'border-box',
              borderRadius: 2,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.05)',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(15, 23, 42, 0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: accentColor,
                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.18)' : 'rgba(0, 174, 239, 0.18)',
              },
              '& .MuiOutlinedInput-root': {
                width: '100%',
                height: '100%',
                minHeight: 'unset',
                borderRadius: 2,
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                margin: 0,
                boxSizing: 'border-box',
                '&.Mui-focused': {
                  border: 'none',
                  boxShadow: 'none',
                },
                '& .MuiSelect-icon': {
                  display: 'none',
                },
                '& fieldset': {
                  border: 'none',
                  display: 'none',
                },
              },
            }}
          >
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              IconComponent={() => null}
              renderValue={(value) => {
                const langCode = getLanguageCode(value);
                const countryCode = languageFlags[langCode] || languageFlags[value] || 'gb';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <FlagIcon countryCode={countryCode} size={20} />
                  </Box>
                );
              }}
              sx={{
                width: '100%',
                height: '100%',
                padding: 0,
                margin: 0,
                boxSizing: 'border-box',
                '& .MuiSelect-select': {
                  padding: '0 !important',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '42px !important',
                  height: '42px !important',
                  width: '42px !important',
                  boxSizing: 'border-box',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 3,
                    mt: 1,
                    boxShadow: isDark ? '0 20px 40px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 174, 239, 0.1)',
                    backdropFilter: 'blur(20px)',
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                  },
                },
              }}
            >
              {Object.entries(languageFlags).map(([lang, flag]) => (
                <MenuItem key={lang} value={lang}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                    <FlagIcon countryCode={flag} size={32} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <Box
              component="img"
              src="/heyhotelai-logo.png"
              alt="HeyHotel.AI"
              sx={{ height: 60, width: 'auto', objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: accentColor }}>
            {t('login.staffLoginTitle') || t('login.staffLogin')}
          </Typography>
          <Typography variant="body2" sx={{ color: secondaryTextColor }}>
            {t('login.staffLoginSubtitle') || t('login.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              mb: 3,
              borderRadius: 2.5,
              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)',
              animation: 'slideDown 0.3s ease-out',
              '@keyframes slideDown': {
                from: { opacity: 0, transform: 'translateY(-10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
          {/* Hotel Selection (Optional for Staff) */}
          <FormControl
            fullWidth
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.9)',
                color: isDark ? '#f8fafc' : '#1f2937',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
                  transform: 'translateY(-1px)',
                  '& fieldset': {
                    borderColor: accentColor,
                    borderWidth: 2,
                  },
                },
                '&.Mui-focused': {
                  backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
                  '& fieldset': {
                    borderColor: accentColor,
                    borderWidth: 2.5,
                  },
                },
                '& fieldset': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                  borderWidth: 1.5,
                },
              },
              '& .MuiInputLabel-root': {
                fontWeight: 600,
                color: isDark ? 'rgba(248, 250, 252, 0.7)' : '#6b7280',
                '&.Mui-focused': {
                  color: accentColor,
                  fontWeight: 700,
                },
              },
            }}
          >
            <InputLabel id="hotel-select-label">{t('login.hotel')}</InputLabel>
            <Select
              labelId="hotel-select-label"
              id="hotel-select"
              value={selectedHotel}
              label={t('login.hotel')}
              onChange={(e) => setSelectedHotel(e.target.value)}
              disabled={hotelsLoading}
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: '#9ca3af', fontSize: '1.25rem' }} />
                      <Typography sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
                        {t('login.none')}
                      </Typography>
                    </Box>
                  );
                }
                const hotel = hotels.find(h => h.id === value);
                if (!hotel) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: '#9ca3af', fontSize: '1.25rem' }} />
                      <Typography sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
                        {t('login.none')}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HotelIcon sx={{ color: accentColor, fontSize: '1.25rem' }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: primaryTextColor, lineHeight: 1.2 }}>
                        {hotel.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: secondaryTextColor, fontSize: '0.7rem', lineHeight: 1.2 }}>
                        {hotel.code}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    borderRadius: 3,
                    mt: 1,
                    boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 174, 239, 0.1)',
                    backdropFilter: 'blur(20px)',
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <BusinessIcon sx={{ color: '#9ca3af', fontSize: '1.25rem' }} />
                  <Typography sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
                    {t('login.none')}
                  </Typography>
                </Box>
              </MenuItem>
              {hotels.map((hotel) => (
                <MenuItem key={hotel.id} value={hotel.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.12)',
                        color: accentColor,
                      }}
                    >
                      <HotelIcon sx={{ fontSize: '1.5rem' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: primaryTextColor,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {hotel.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: secondaryTextColor, fontSize: '0.75rem', display: 'block', mt: 0.25 }}>
                        Code: {hotel.code}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Email Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t('login.email')}
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) validateEmail(e.target.value);
            }}
            onBlur={(e) => validateEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            sx={textFieldSx}
          />

          {/* Password Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t('login.password')}
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            inputRef={passwordInputRef}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
            }}
            onBlur={(e) => validatePassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError || t('login.minimumChars')}
            sx={textFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              backgroundColor: accentColor,
              boxShadow: '0 18px 35px rgba(0, 174, 239, 0.35)',
              '&:hover': {
                backgroundColor: accentHover,
                boxShadow: '0 18px 40px rgba(0, 174, 239, 0.45)',
              },
              '&:disabled': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('login.signIn')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default StaffLoginPage;